/**
 * UserMenu — the top-right account control shared by every authenticated
 * SystemDox surface (Console, Capture, Launch, BookViewer).
 *
 * The dropdown itself is now `@puglieseweb/shared-react-ui`'s: this app had a
 * third variant of a menu the portal and the ops console already had twice,
 * with its own colours, its own order and no sign-out-everywhere. What stays
 * here is what is SystemDox's — the optional Notifications and Settings links,
 * and fetching the avatar, which lives in the user-profile service rather than
 * in the token and so has to be read once per session and written back through
 * `setAvatarUrl` so the whole app stays in sync.
 *
 * The theme control is the shared component's `theme` row now; it reads and
 * writes the same `@puglieseweb/shared-react-ui` theme preference it always
 * did, so a person's light/dark/auto choice survives this change.
 *
 * No Sign out everywhere yet. It needs `authApiUrl` in `configureAuth`, which
 * the app sets since #1316 (lib/authConfig, for the Welcome step's name save),
 * so offering the entry is now a product decision rather than a missing
 * endpoint.
 */

import { useEffect } from 'react';
import { Bell, Settings } from 'lucide-react';
import { UserMenu as SharedUserMenu } from '@puglieseweb/shared-react-ui';
import type { UserMenuItem } from '@puglieseweb/shared-react-ui';
import { useAuthContext } from '@/contexts/AuthContext';
import { avatarApi } from '@/services/avatarApi';

export function UserMenu({
  settingsTo,
  notificationsTo,
  className,
}: {
  /** When set, a Settings link is shown in the menu. */
  settingsTo?: string;
  /** When set, a Notifications link (full history) is shown in the menu. */
  notificationsTo?: string;
  className?: string;
}) {
  const { user, logout, setAvatarUrl } = useAuthContext();

  // Avatars are presigned URLs served by the user-profile API, not the token.
  useEffect(() => {
    if (user && !user.avatarUrl) {
      avatarApi
        .getAvatar()
        .then((res) => {
          if (res.exists && res.avatarUrl) setAvatarUrl(res.avatarUrl);
        })
        .catch(() => {
          // No avatar yet, or profile service unavailable — show initials.
        });
    }
  }, [user, setAvatarUrl]);

  const items: UserMenuItem[] = [
    ...(notificationsTo
      ? [{ key: 'notifications', label: 'Notifications', icon: Bell, to: notificationsTo }]
      : []),
    ...(settingsTo ? [{ key: 'settings', label: 'Settings', icon: Settings, to: settingsTo }] : []),
  ];

  return (
    <SharedUserMenu
      className={className}
      user={user ?? undefined}
      items={items}
      onUploadAvatar={async (file) => setAvatarUrl(await avatarApi.upload(file))}
      theme
      onSignOut={logout}
      labels={{ changeAvatar: 'Change photo' }}
    />
  );
}
